import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Trophy, Timer, Check, X, Flame, Users, Share2, Copy, Crown, Sparkles } from "lucide-react";
import { getChallenge, submitChallengeResult, type ChallengeResultRow } from "@/lib/challenges.functions";
import { logQuizSession } from "@/lib/quiz-stats.functions";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { track } from "@/lib/analytics";

export const Route = createFileRoute("/challenge/$code")({
  head: ({ params }) => ({
    meta: [
      { title: `Quiz challenge · ${params.code} - Smartasaurus` },
      { name: "description", content: "A friend challenged you to a Smartasaurus quiz round. Same 10 questions. Who wins?" },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "You've been challenged 🧠" },
      { property: "og:description", content: "Same 10 questions. Same rules. Beat their score." },
    ],
  }),
  loader: async ({ params }) => {
    const res = await getChallenge({ data: { code: params.code } });
    return res;
  },
  component: ChallengePage,
});

const TIMER = 18;

function playerKey() {
  if (typeof window === "undefined") return "server";
  try {
    let k = localStorage.getItem("smartasaurus.player.key");
    if (!k) { k = `p_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`; localStorage.setItem("smartasaurus.player.key", k); }
    return k;
  } catch { return `p_${Math.random().toString(36).slice(2, 10)}`; }
}

function prepQuestion(q: any) {
  const options: string[] = Array.isArray(q.options) ? q.options : [];
  const correct = options[q.answer_index];
  const shuffled = options.map((v, i) => ({ v, i })).sort(() => Math.random() - 0.5);
  const newCorrect = shuffled.findIndex((o) => o.v === correct);
  return {
    id: q.id as string,
    slug: q.slug as string,
    question: q.question as string,
    options: shuffled.map((o) => o.v),
    correctIdx: newCorrect,
    explanation: (q.explanation as string) ?? "",
  };
}

function ChallengePage() {
  const data = Route.useLoaderData();
  const navigate = useNavigate();
  const submit = useServerFn(submitChallengeResult);
  const logSessionFn = useServerFn(logQuizSession);
  const startRef = useRef<number>(Date.now());

  if (!data || !(data as any).ok) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <div className="mx-auto max-w-lg px-4 py-16 text-center">
          <div className="text-6xl">🕳️</div>
          <h1 className="mt-4 font-display text-3xl uppercase">Challenge not found</h1>
          <p className="mt-2 text-sm text-muted-foreground">This link expired or was mistyped.</p>
          <Link to="/play" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-xs font-bold uppercase tracking-widest text-primary-foreground">
            <ArrowLeft className="h-4 w-4" /> Play Smartasaurus
          </Link>
        </div>
      </div>
    );
  }

  const challenge = (data as any).challenge;
  const quizzes = (data as any).quizzes as any[];
  const initialResults = (data as any).results as ChallengeResultRow[];

  const [name, setName] = useState<string>(() => { try { return localStorage.getItem("smartasaurus.player.name") ?? ""; } catch { return ""; } });
  const [phase, setPhase] = useState<"intro" | "playing" | "results">("intro");
  const [results, setResults] = useState<ChallengeResultRow[]>(initialResults);

  const round = useMemo(() => quizzes.map(prepQuestion), [quizzes]);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const [locked, setLocked] = useState(false);
  const [score, setScore] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [timeLeft, setTimeLeft] = useState(TIMER);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  function clearTimer() { if (timerRef.current) clearInterval(timerRef.current); timerRef.current = null; }
  useEffect(() => () => clearTimer(), []);

  useEffect(() => {
    if (phase !== "playing" || locked) return;
    setTimeLeft(TIMER);
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => { if (t <= 1) { clearTimer(); setTimeout(() => lock(null), 0); return 0; } return t - 1; });
    }, 1000);
    return () => clearTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, phase]);

  function lock(choice: number | null) {
    if (locked) return;
    clearTimer();
    setLocked(true); setPicked(choice);
    const cur = round[idx];
    const ok = choice !== null && choice === cur.correctIdx;
    if (ok) {
      const ns = streak + 1;
      setStreak(ns); setBestStreak((b) => Math.max(b, ns));
      const speed = Math.max(0, Math.min(3, Math.round((timeLeft / TIMER) * 3)));
      const combo = Math.min(5, ns);
      setScore((s) => s + (5 + speed) + (combo - 1) * 2);
      setCorrect((c) => c + 1);
    } else { setStreak(0); }
  }

  function next() {
    if (idx + 1 >= round.length) finish();
    else { setIdx((i) => i + 1); setPicked(null); setLocked(false); setTimeLeft(TIMER); }
  }

  async function finish() {
    clearTimer();
    // Ensure we have a display name
    let display = name.trim();
    if (!display) {
      const { data: sess } = await supabase.auth.getUser();
      display = (sess?.user?.user_metadata as any)?.name || (sess?.user?.email?.split("@")[0]) || "Anonymous";
    }
    try { localStorage.setItem("smartasaurus.player.name", display); } catch {}
    try {
      const res: any = await submit({
        data: {
          code: challenge.code, player_key: playerKey(), display_name: display,
          score, correct, best_streak: bestStreak,
        },
      });
      if (res?.ok) setResults(res.results as ChallengeResultRow[]);
    } catch { /* ignore */ }
    try {
      const { data: u } = await supabase.auth.getUser();
      logSessionFn({
        data: {
          session_key: playerKey(),
          category: challenge.category,
          difficulty: challenge.difficulty as any,
          score, correct, total: round.length, best_streak: bestStreak,
          duration_ms: Date.now() - startRef.current,
          quiz_ids: round.map((r) => r.id),
          source: "challenge",
          challenge_code: challenge.code,
          user_id: u?.user?.id ?? null,
        },
      }).catch(() => {});
    } catch { /* ignore */ }
    track("challenge_played", { code: challenge.code, score, correct });
    setPhase("results");
  }

  function copyLink() {
    const url = `${window.location.origin}/challenge/${challenge.code}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Copied")).catch(() => {});
  }

  if (phase === "intro") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/20 via-background to-background text-foreground">
        <div className="mx-auto max-w-lg px-4 py-10 sm:py-14">
          <Link to="/play" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Solo play
          </Link>
          <div className="mt-6 rounded-3xl border border-primary/40 bg-card/60 p-6 text-center backdrop-blur">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/20 text-primary"><Users className="h-6 w-6" /></div>
            <div className="mt-3 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">You've been challenged</div>
            <h1 className="mt-2 font-display text-3xl uppercase leading-tight sm:text-4xl">
              {challenge.host_name || "A friend"} vs. you
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {round.length} questions · {challenge.difficulty} · {TIMER}s per card
            </p>
            {challenge.host_score !== null && (
              <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-500/15 px-3 py-1.5 text-xs font-bold text-amber-300 ring-1 ring-amber-300/40">
                <Trophy className="h-3.5 w-3.5" /> Score to beat: {challenge.host_score}
              </div>
            )}
            <div className="mt-6 text-left">
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Your name (shown on leaderboard)</label>
              <input
                value={name} onChange={(e) => setName(e.target.value)} maxLength={40}
                placeholder="Anonymous"
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              />
            </div>
            <button
              onClick={() => setPhase("playing")}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-5 py-3.5 text-sm font-bold uppercase tracking-wide text-primary-foreground hover:brightness-110"
            >
              <Sparkles className="h-4 w-4" /> Accept challenge
            </button>
          </div>

          {results.length > 0 && (
            <div className="mt-6 rounded-2xl border border-border bg-card/50 p-4">
              <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Leaderboard so far</div>
              <Leaderboard results={results} hostName={challenge.host_name} hostScore={challenge.host_score} />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (phase === "results") {
    const hostScore = challenge.host_score ?? 0;
    const beat = score > hostScore;
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/20 via-background to-background text-foreground">
        <div className="mx-auto max-w-lg px-4 py-10">
          <div className="rounded-3xl border border-primary/40 bg-card/60 p-6 text-center backdrop-blur">
            <div className="text-6xl">{beat ? "🏆" : correct >= round.length / 2 ? "🧠" : "🌱"}</div>
            <h2 className="mt-3 font-display text-3xl uppercase">{beat ? "You won" : "Nice try"}</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {correct}/{round.length} correct · streak {bestStreak}×
            </p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <div className="rounded-xl border border-border bg-background/40 p-3">
                <div className="text-[10px] font-bold uppercase text-muted-foreground">Your score</div>
                <div className="font-display text-2xl">{score}</div>
              </div>
              <div className="rounded-xl border border-border bg-background/40 p-3">
                <div className="text-[10px] font-bold uppercase text-muted-foreground">{challenge.host_name ?? "Host"}</div>
                <div className="font-display text-2xl">{hostScore}</div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-2">
              <button onClick={copyLink} className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/50 bg-primary/10 px-5 py-3 text-xs font-bold uppercase tracking-widest text-primary hover:bg-primary/20">
                <Copy className="h-4 w-4" /> Copy link - challenge someone else
              </button>
              <button onClick={() => navigate({ to: "/play" })} className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 px-5 py-3 text-xs font-bold uppercase tracking-widest hover:bg-white/20">
                Start my own round →
              </button>
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-border bg-card/50 p-4">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Leaderboard</div>
            <Leaderboard results={results} hostName={challenge.host_name} hostScore={challenge.host_score} />
          </div>
        </div>
      </div>
    );
  }

  // playing
  const q = round[idx];
  const progress = ((idx + (locked ? 1 : 0)) / round.length) * 100;
  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white">
      <div className="relative mx-auto flex min-h-screen max-w-2xl flex-col px-4 py-4">
        <div className="flex items-center justify-between">
          <button onClick={() => { clearTimer(); setPhase("intro"); }} className="inline-flex items-center gap-1 rounded-full bg-black/40 px-3 py-1.5 text-[10px] font-bold uppercase text-white/80">
            <ArrowLeft className="h-3 w-3" /> Quit
          </button>
          <div className="font-mono text-xs font-bold uppercase text-white/80">{idx + 1}/{round.length}</div>
          <span className="inline-flex items-center gap-1 rounded-full bg-black/40 px-3 py-1.5 text-xs font-bold"><Trophy className="h-3 w-3 text-amber-300" /> {score}</span>
        </div>
        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white/15">
          <div className="h-full bg-gradient-to-r from-amber-300 to-white transition-[width]" style={{ width: `${progress}%` }} />
        </div>
        <div className="mt-2 flex items-center gap-2">
          <Timer className={`h-3.5 w-3.5 ${timeLeft <= 4 ? "text-red-300 animate-pulse" : "text-white/70"}`} />
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/10">
            <div className={`h-full transition-[width] duration-1000 ease-linear ${timeLeft <= 4 ? "bg-red-400" : "bg-amber-300"}`} style={{ width: `${(timeLeft / TIMER) * 100}%` }} />
          </div>
          <span className="w-6 text-right font-mono text-xs tabular-nums">{timeLeft}</span>
        </div>

        <div className="mt-6 rounded-3xl border border-white/15 bg-black/30 p-5 backdrop-blur">
          <h2 className="font-display text-xl uppercase leading-tight sm:text-2xl">{q.question}</h2>
          <div className="mt-4 grid gap-2">
            {q.options.map((opt, i) => {
              const isCorrect = i === q.correctIdx;
              const isPicked = picked === i;
              return (
                <button
                  key={i}
                  onClick={() => lock(i)}
                  disabled={locked}
                  className={`flex items-center justify-between rounded-2xl border px-4 py-3 text-left text-sm font-semibold transition ${
                    !locked ? "border-white/20 bg-white/5 hover:bg-white/15"
                      : isCorrect ? "border-emerald-300 bg-emerald-400/20"
                      : isPicked ? "border-red-300 bg-red-400/20" : "border-white/10 bg-white/5 opacity-60"
                  }`}
                >
                  <span>{opt}</span>
                  {locked && isCorrect && <Check className="h-4 w-4 text-emerald-300" />}
                  {locked && isPicked && !isCorrect && <X className="h-4 w-4 text-red-300" />}
                </button>
              );
            })}
          </div>

          {locked && (
            <div className="mt-4 rounded-2xl bg-white/10 p-3 text-sm text-white/90">
              {q.explanation}
            </div>
          )}
        </div>

        <div className="mt-auto pt-4">
          <button
            onClick={next}
            disabled={!locked}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-5 py-4 text-sm font-bold uppercase tracking-wide text-black disabled:opacity-40"
          >
            {idx + 1 >= round.length ? "See results" : "Next question →"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Leaderboard({
  results, hostName, hostScore,
}: {
  results: ChallengeResultRow[];
  hostName?: string | null;
  hostScore?: number | null;
}) {
  const rows = results.slice().sort((a, b) => b.score - a.score);
  return (
    <ol className="space-y-1.5">
      {hostScore !== null && hostScore !== undefined && (
        <li className="flex items-center justify-between rounded-xl border border-amber-300/40 bg-amber-500/10 px-3 py-2 text-sm">
          <span className="flex items-center gap-2"><Crown className="h-3.5 w-3.5 text-amber-300" /> {hostName || "Host"} (host)</span>
          <span className="font-mono font-bold text-amber-200">{hostScore}</span>
        </li>
      )}
      {rows.length === 0 ? (
        <li className="text-xs text-muted-foreground">No results yet - be the first.</li>
      ) : (
        rows.map((r, i) => (
          <li key={r.id}
            className={`flex items-center justify-between rounded-xl px-3 py-2 text-sm ${
              r.is_current_player ? "border border-primary/50 bg-primary/10" : "bg-background/40"
            }`}
          >
            <span className="flex items-center gap-2">
              <span className="w-5 text-right text-xs text-muted-foreground">{i + 1}.</span>
              {r.display_name}
              {r.best_streak >= 3 && <span className="inline-flex items-center gap-1 rounded-full bg-orange-400/20 px-1.5 text-[10px] text-orange-300"><Flame className="h-3 w-3" />{r.best_streak}×</span>}
            </span>
            <span className="font-mono font-bold">{r.score}</span>
          </li>
        ))
      )}
    </ol>
  );
}
